import { CoralogixBrowserSdkConfig, CoralogixRum } from '@coralogix/browser';
import {
    ANY_MEWS_URL,
    BLOCK_OBSERVABILITY_ATTRIBUTE,
    CORALOGIX_DOMAIN,
    CORALOGIX_PUBLIC_KEYS,
    OBSERVABILITY_UNMASK_ATTRIBUTE,
} from './constants';
import { ObservabilityServiceLabels, setLabelsStrict, setUserContext, UserContext } from './coralogix';

interface CreateInstrumentationOptions {
    /**
     * Controls how much Real User Monitoring (RUM) data is collected and sent.
     * When enabled, all user sessions are tracked and recorded. When disabled, only a portion of sessions is sampled.
     * See the implementation for details.
     */
    fullSamplingEnabled: boolean;

    service: string;
    version: string;
    env: string;

    /**
     * Returns additional data to attach to telemetry events.
     * Optional if context and labels are provided elsewhere (e.g. via `Observability` class or `useObservability` hook).
     */
    getExtra?: () => Record<string, string | null> & UserContext & ObservabilityServiceLabels;

    /**
     * Whether to update labels from `getExtra` automatically before sending each event.
     *
     * @default true
     */
    updateLabelsOnBeforeSend?: boolean;

    /**
     * Whether to update user context from `getExtra` automatically before sending each event.
     *
     * @default true
     */
    updateUserContextOnBeforeSend?: boolean;
}

interface InstrumentationInitOptions {
    fullSamplingEnabled: boolean;
}

let config: CoralogixBrowserSdkConfig | undefined;

export const createInstrumentation = ({
    fullSamplingEnabled,
    service,
    version,
    env,
    getExtra = () => ({}),
    updateLabelsOnBeforeSend = true,
    updateUserContextOnBeforeSend = true,
}: CreateInstrumentationOptions) => {
    const publicKey = CORALOGIX_PUBLIC_KEYS[env as keyof typeof CORALOGIX_PUBLIC_KEYS];

    if (!publicKey) {
        return;
    }

    config = {
        application: service,
        environment: env,
        public_key: publicKey,
        coralogixDomain: CORALOGIX_DOMAIN,
        traceParentInHeader: {
            enabled: true,
            options: {
                propagateTraceHeaderCorsUrls: [ANY_MEWS_URL],
            },
        },
        sessionConfig: {
            alwaysTrackSessionsWithErrors: true,
            keepSessionAfterReload: true,
            sessionSampleRate: fullSamplingEnabled ? 100 : 10,
        },
        sessionRecordingConfig: {
            enable: true,
            autoStartSessionRecording: true,
            recordConsoleEvents: true,
            recordCrossOriginIframes: false,
            blockSelector: `[${BLOCK_OBSERVABILITY_ATTRIBUTE}]`,
            maskAllInputs: true,
            maskTextSelector: '*',
            maskTextFn: (text: string, element: HTMLElement | null) => {
                if (element?.hasAttribute(OBSERVABILITY_UNMASK_ATTRIBUTE)) {
                    return text;
                }
                return '*'.repeat(text.length);
            },
            sessionRecordingSampleRate: fullSamplingEnabled ? 100 : 20,
        },
        ignoreUrls: [
            /.*fullview\.io.*/, // Fullview session recording
            /.*visualstudio\.com.*/, // Application Insights
            /.*newrelic\.com.*/, // New Relic logging
            /.*nr-data\.net.*/, // New Relic error tracking
            /.*aptrinsic\.com.*/, // Gainsight/Aptrinsic analytics
            /.*getbeamer\.com.*/, // Beamer notifications
            /.*launchdarkly\.com.*/, // LaunchDarkly feature flags
            /.*cookiefirst\.com.*/, // CookieFirst consent management
        ],
        version,
        beforeSend: (event) => {
            const extra = getExtra();

            if (updateUserContextOnBeforeSend) {
                setUserContext(extra);
            }

            if (updateLabelsOnBeforeSend) {
                setLabelsStrict({ env, ...extra });
            }

            return event;
        },
    };

    CoralogixRum.init(config);
};

export const reinitializeInstrumentation = ({ fullSamplingEnabled }: InstrumentationInitOptions) => {
    if (!config) {
        return;
    }

    CoralogixRum.shutdown();

    CoralogixRum.init({
        ...config,
        sessionConfig: {
            ...config.sessionConfig,
            sessionSampleRate: fullSamplingEnabled ? 100 : 10,
        },
        // @ts-expect-error
        sessionRecordingConfig: {
            ...config.sessionRecordingConfig,
            sessionRecordingSampleRate: fullSamplingEnabled ? 100 : 20,
        },
    });
};
